/**
 * SignInForm.jsx
 * Created by michaelbray on 2/14/18.
 * Updated by Destin Frasier on 1/11/19.
 */

import React from 'react';
import PropTypes from 'prop-types';

import * as Icons from '../../../sharedComponents/icons/Icons';
import { FlexCol, FlexRow } from '../../../sharedComponents/FlexGrid';
import Button, { ButtonGroup } from '../../../sharedComponents/Button';
import Label from '../../../sharedComponents/Label';
import Loading from '../../../sharedComponents/Loading';
import Heading from '../../../sharedComponents/Heading';
import Notice from '../../../notifications/Notice';
import { LOGIN_NOTICE_PROPS } from '../../constants';
import DisplayHtmlContent from '../../../sharedComponents/DisplayHtmlContent';

const propTypes = {
    disableAccountFeatures: PropTypes.bool,
    errorText: PropTypes.string,
    create: PropTypes.func,
    reset: PropTypes.func,
    confirmSignIn: PropTypes.func,
    mfaRequired: PropTypes.bool,
    mfaValidation: PropTypes.bool,
    loginNotice: LOGIN_NOTICE_PROPS
};

export default class SignInForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            email: '',
            password: '',
            processing: false,
            noticedClosed: false
        };

        this.emailTextChange = this.emailTextChange.bind(this);
        this.passwordTextChange = this.passwordTextChange.bind(this);
        this.submitForm = this.submitForm.bind(this);
    }

    componentDidMount() {
        this.email.focus();
        this.showLoginModal = true;
    }

    emailTextChange(e) {
        this.setState({
            email: e.target.value
        });
    }

    passwordTextChange(e) {
        this.setState({
            password: e.target.value
        });
    }

    submitForm(e) {
        e.preventDefault();
        this.setState({
            processing: true
        })

        this.props.confirmSignIn({
            email: this.state.email,
            password: this.state.password
        });
    }

    closeNotice = () => {
        this.setState({ noticedClosed: true });
    }

    render() {
        let error = null;
        let loading = null;
        let processing = this.state.processing;
        if (this.props.errorText || this.props.mfaRequired) {
            processing = false;
        }
        if (processing) {
            loading = (
                <div className="rec-sign-loading">
                    <Loading />
                </div>
            )
        }

        if (this.props.errorText) {
            error = (
                <div className="rec-notification-error">
                    <span className="rec-notification-icon">
                        <Icons.IconError className="rec-icon-error" />
                    </span>
                    <div className="rec-notification-body">
                        <p>{this.props.errorText}</p>
                    </div>
                </div>
            );
        }

        let mfa = null;
        if (this.props.mfaRequired) {
            mfa = (
                <div className="rec-notification-warning rec-mfa-required-message">
                    <span className="rec-notification-icon">
                        <Icons.IconWarning className="rec-icon-warning" />
                    </span>
                    We don&apos;t recognize the device you&apos;re trying to login from.
                    Please check your email to verify your identity and authorize this device.
                </div>
            );
        }

        let mfaValidationText = null;
        let reset = null;
        let create = null;
        if (this.props.mfaValidation) {
            mfaValidationText = (
                <div className="rec-notification-info">
                    <span className="rec-notification-icon">
                        <Icons.IconInfo className="rec-icon-info" />
                    </span>
                    Your device has been successfully validated, please log in to complete this process.
                </div>
            );
        }
        else {
            reset = (
                <ButtonGroup align="right">
                    <Button
                        appearance="link"
                        onClick={this.props.reset}
                        disabled={this.props.disableAccountFeatures}
                        title="Reset Password"
                        aria-label="Reset Password">
                        Reset Password
                    </Button>
                </ButtonGroup>
            );
            create = (
                <div className="rec-acct-modal-existing-account">
                    <p>Don&apos;t have an account?</p>
                    <Button
                        appearance="link"
                        onClick={this.props.create}
                        disabled={this.props.disableAccountFeatures}
                        title="Sign Up"
                        aria-label="Sign Up">
                        Sign Up
                    </Button>
                </div>
            );
        }

        let loginDisabled = null;
        if (this.props.disableAccountFeatures) {
            loginDisabled = (
                <Notice
                    noticeType="error"
                    closeNotice={this.closeNotice}
                    showClose>
                    <div className="rec-notification-title">
                        Please be aware that you cannot make or change any
                        reservations nor can you log in to your account or create
                        a new one. Account access and reservations are expected to
                        be available starting on October 5 at 10 a.m. (Eastern Time).
                    </div>
                </Notice>
            );
        }

        let loginNoticeComponent = null;
        if (this.props.loginNotice.notice_enabled) {
            let decodedLoginNotice = "";
            try {
                decodedLoginNotice = decodeURIComponent(this.props.loginNotice.notice_body)
            }
            catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                decodedLoginNotice = "";
                this.state.noticedClosed = true
            }

            loginNoticeComponent = (
                <div className="rec-legacy-users-message">
                    <Notice
                        noticeType="warning"
                        showNotice={!this.state.noticedClosed}
                        closeNotice={this.closeNotice}
                        showClose>
                        <div className="h6">{this.props.loginNotice.notice_title}</div>
                        { /* The banner_body is URI-encoded HTML */}
                        <DisplayHtmlContent html={decodedLoginNotice} />
                    </Notice>
                </div>
            );
        }

        let loginFormColumnProps = {
            md: 8,
            mdOffset: 2,
            xsOrder: null,
            mdOrder: null
        };

        let centerLineColumnProps = {
            className: "hide"
        };

        let noticeColumnProps = {
            className: "hide"
        };

        let loginMsgProps = {
            md: 8,
            mdOffset: 2
        };

        if (!this.state.noticedClosed && this.props.loginNotice.notice_enabled) {
            loginFormColumnProps = {
                md: 5,
                xsOrder: 3,
                mdOrder: 1
            };

            centerLineColumnProps = {
                md: 1,
                mdOrder: 2,
                className: "rec-acct-modal-split-line-wrap"
            };

            noticeColumnProps = {
                md: 6,
                alignSelf: "center",
                xsOrder: 1,
                mdOrder: 3
            };

            loginMsgProps = {
                md: 12,
                mdOffset: null
            };
        }

        return (
            <FlexRow justifyContent="center">
                <FlexCol sm={8} lg={6} className="rec-acct-modal-log-in-body">
                    <Heading
                        headingLevel={1}
                        appearance="display"
                        className="rec-acct-modal-title"
                        id="signInModalHeading">
                        Log In
                    </Heading>
                    <FlexRow>
                        <FlexCol {...loginMsgProps}>
                            {create}
                            {loginDisabled}
                            {loading}
                            {error}
                            {mfa}
                            {mfaValidationText}
                        </FlexCol>
                        <FlexCol {...loginFormColumnProps}>
                            <form onSubmit={this.submitForm}>
                                <div className="form-item-wrap">
                                    <Label
                                        htmlFor="rec-acct-sign-in-email-address">
                                        Email
                                    </Label>
                                    <input
                                        id="rec-acct-sign-in-email-address"
                                        disabled={this.props.disableAccountFeatures}
                                        ref={(email) => this.email = email}
                                        onChange={(event) => this.emailTextChange(event)}
                                        className="rec-acct-email" />
                                </div>
                                <div className="form-item-wrap">
                                    <Label
                                        htmlFor="rec-acct-sign-in-password">
                                        Password
                                    </Label>
                                    <input
                                        id="rec-acct-sign-in-password"
                                        disabled={this.props.disableAccountFeatures}
                                        type="password"
                                        ref={(password) => this.password = password}
                                        onChange={(event) => this.passwordTextChange(event)}
                                        className="rec-acct-password"
                                        autoComplete="off" />
                                </div>
                                <Button
                                    shouldFitContainer
                                    size="lg"
                                    type="submit"
                                    className="rec-acct-sign-in-btn"
                                    disabled={processing || this.props.disableAccountFeatures}
                                    title="Log In"
                                    aria-label="Log In">
                                    Log In
                                </Button>
                            </form>
                            {reset}
                            <div className="rec-captcha-notice">
                                This site is protected by reCAPTCHA and the Google&nbsp;
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                &nbsp;and&nbsp;
                                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
                            </div>
                        </FlexCol>
                        <FlexCol {...centerLineColumnProps}>
                            <div className="rec-acct-modal-split-line" />
                        </FlexCol>
                        <FlexCol {...noticeColumnProps}>
                            {loginNoticeComponent}
                        </FlexCol>
                    </FlexRow>
                </FlexCol>
            </FlexRow>
        );
    }
}

SignInForm.propTypes = propTypes;
